/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useCallback } from "react";
import ReactECharts from "src/utils/EChartsReact";
import { formatNumber, convertStringToNumeric } from "src/pages/Insights/transformer/dashboardUtils";
import { getCommonTooltipConfig, buildChartTooltip, useTooltipCoordination } from "../tooltipFormatter";
import { hasAnyStatMarkers, resolveStatMarkerRows, buildHStackedMarkPoints } from "./../../components/StatMarkerUtils";
import { chartConfig } from "../../chartConfig";
import { IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig } from "../../ChartScaleContext";
import { isLightColor, getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_PLACEHOLDER, SUPPRESSED_VALUE_COLOR } from "../chartHelpers";

interface IMiniHStackedBarChartProps {
	series: {
		name: string;
		value: number;
		n_size: number | string;
		stat_marker?: IStatMarker | null;
	}[];
	colors: Record<string, string>;
	labelFormat: string;
	xAxisDomain: { min: number; max: number; interval: number };
	rowHeight: number;
	containerWidth?: number;
	chartHeight?: number;
	statTestConfig?: IStatTestConfig | null;
}

// ─────────────────────────────────────────────────────────────
// FIX 1: Accurate text width measurement using canvas
// ─────────────────────────────────────────────────────────────
const _measureCanvas = (() => {
	let canvas: HTMLCanvasElement | null = null;
	return () => {
		if (!canvas) {
			canvas = document.createElement("canvas");
		}
		return canvas.getContext("2d")!;
	};
})();

/**
 * Measures the actual pixel width of a string at a given font size.
 * Uses a cached offscreen canvas — no DOM reflow.
 */
const measureTextWidth = (text: string, fontSize: number, fontWeight: string = "normal"): number => {
	const ctx = _measureCanvas();
	// Match ECharts' default font stack
	ctx.font = `${fontWeight} ${fontSize}px sans-serif`;
	return ctx.measureText(text).width;
};

// ─────────────────────────────────────────────────────────────
// FIX 2: Pre-pass to resolve which labels to show, preventing
//         neighbor collisions — not just per-segment checks.
// ─────────────────────────────────────────────────────────────

interface SegmentMeasurement {
	name: string;
	formattedVal: string;
	processedValue: number;
	segmentPixelWidth: number;
	textPixelWidth: number;
	isNoData: boolean;
}

/**
 * Two-pass label visibility resolver.
 *
 * Pass 1: Mark labels that clearly don't fit (text wider than segment).
 * Pass 2: For remaining visible labels, check that adjacent visible labels
 *          don't collide at segment boundaries (with a padding buffer).
 */
const resolveLabelVisibility = (
	measurements: SegmentMeasurement[],
	labelPaddingPx: number = 6, // horizontal padding inside segment (3px each side)
): boolean[] => {
	const visibility = measurements.map((m) => {
		if (m.isNoData || m.processedValue === 0) return false;
		// Pass 1: does the text physically fit inside the segment?
		return m.textPixelWidth + labelPaddingPx <= m.segmentPixelWidth;
	});

	// Pass 2: check neighbor collisions at segment boundaries.
	// Even if a label "fits" inside its segment, the centered position
	// may cause it to visually collide with a neighbor's centered label.
	for (let i = 1; i < measurements.length; i++) {
		if (!visibility[i] || !visibility[i - 1]) continue;

		const prev = measurements[i - 1];
		const curr = measurements[i];

		// Each label is centered in its segment. Compute the gap between
		// the right edge of the prev label and the left edge of the curr label.
		const prevLabelRight = prev.segmentPixelWidth / 2 + prev.textPixelWidth / 2;
		const currLabelLeft = curr.segmentPixelWidth / 2 - curr.textPixelWidth / 2;
		const gapBetweenLabels = prev.segmentPixelWidth - prevLabelRight + currLabelLeft;

		if (gapBetweenLabels < 2) {
			// Collision detected — hide the label on the SMALLER segment
			if (prev.segmentPixelWidth <= curr.segmentPixelWidth) {
				visibility[i - 1] = false;
			} else {
				visibility[i] = false;
			}
		}
	}

	return visibility;
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

const MiniHStackedBarChart: React.FC<IMiniHStackedBarChartProps> = ({ series, colors, labelFormat, xAxisDomain, rowHeight, containerWidth, chartHeight, statTestConfig }) => {
	const scaled = useScaledConfig();

	const config = useMemo(() => {
		const hStackedConfig = chartConfig.getHorizontalStackedConfig();
		const colorsConfig = chartConfig.getColorsConfig();
		const layoutConfig = chartConfig.getLayoutConfig();

		const gridLeft = 0;
		const gridRight = scaled.geometry.cellPaddingH;
		const effectiveBarWidth = containerWidth ? Math.max(0, containerWidth - gridLeft - gridRight) : hStackedConfig.barWidthApprox * scaled.scaleFactor;

		return {
			effectiveBarWidth,
			minLabelWidth: hStackedConfig.minLabelWidth * scaled.scaleFactor,
			maxBarWidth: Math.min(hStackedConfig.maxBarWidth, scaled.geometry.maxBarWidth),
			barWidthMultiplier: hStackedConfig.barWidthMultiplier,
			labelFontSize: scaled.typography.dataLabel,
			whiteBg: colorsConfig.backgrounds.white,
			showLabelThreshold: hStackedConfig.showLabelThreshold * scaled.scaleFactor,
			mutedText: colorsConfig.text.muted,
			statMarkerLayout: {
				...hStackedConfig.statMarkerLayout,
				minSegmentWidth: hStackedConfig.statMarkerLayout.minSegmentWidth * scaled.scaleFactor,
			},
			layoutConfig,
		};
	}, [scaled, containerWidth]);

	const { chartRef, onEvents } = useTooltipCoordination();

	const tooltipFormatter = useCallback((params: any) => buildChartTooltip(params, series, labelFormat, statTestConfig, colors), [series, labelFormat, statTestConfig, colors]);

	const dataStatus = useMemo(() => {
		if (!series || series.length === 0) return "no-data";
		const allNoData = series.every((s) => {
			const val = s.value;
			return (
				val === undefined ||
				val === null ||
				(val as unknown) === "No Data" ||
				(val as unknown) === "No data" ||
				(typeof val === "string" && (val as string).toLowerCase() === "no data") ||
				(typeof val === "number" && !isFinite(val))
			);
		});
		if (allNoData) return "no-data";
		// All segments have suppressed n_size (No data or Low N) — none renders.
		// Pick the dominant label: Low N if any segment is Low N, else No data.
		const statuses = series.map((s) => getNSizeStatusLabel(s.n_size));
		if (statuses.every((st) => st !== null)) {
			return statuses.some((st) => st === NSizeLabelText.LowN) ? "low-n" : "no-data";
		}
		const total = series.reduce((sum, s) => {
			return sum + (typeof s.value === "number" && isFinite(s.value) ? s.value : 0);
		}, 0);
		return total === 0 ? "zero-sum" : "valid";
	}, [series]);

	const processedSeries = useMemo(() => {
		const isPercentage = labelFormat.includes("%");
		const valueRange = xAxisDomain.max - xAxisDomain.min;
		const { effectiveBarWidth, labelFontSize } = config;
		const barWidth = Math.max(4, Math.min(config.maxBarWidth * 2.5, Math.round(rowHeight * config.barWidthMultiplier)));

		// ──────────────────────────────────────────────
		// Step 1: Measure all segments
		// ──────────────────────────────────────────────
		const measurements: SegmentMeasurement[] = series.map((s) => {
			const isNoDataValue =
				s.value === undefined ||
				s.value === null ||
				(typeof s.value === "string" && (s.value as string).toLowerCase() === "no data") ||
				(typeof s.value === "number" && !isFinite(s.value));

			const nSizeStatusLabel = getNSizeStatusLabel(s.n_size);
			const suppressValue = nSizeStatusLabel !== null;

			const formattedVal = isNoDataValue || suppressValue ? SUPPRESSED_VALUE_PLACEHOLDER : formatNumber(s.value, labelFormat);
			const processedValue = isPercentage && !isNoDataValue && !suppressValue ? s.value * 100 : isNoDataValue || suppressValue ? 0 : s.value;

			const segmentPixelWidth = valueRange !== 0 ? (processedValue / valueRange) * effectiveBarWidth : 0;

			// FIX: Measure actual text width instead of using a fixed threshold
			const textPixelWidth = isNoDataValue || suppressValue ? 0 : measureTextWidth(formattedVal, labelFontSize);

			return {
				name: s.name,
				formattedVal,
				processedValue,
				segmentPixelWidth,
				textPixelWidth,
				isNoData: isNoDataValue || suppressValue,
			};
		});

		// ──────────────────────────────────────────────
		// Step 2: Resolve visibility with neighbor-aware logic
		// ──────────────────────────────────────────────
		const labelVisibility = resolveLabelVisibility(measurements, 6);

		// ──────────────────────────────────────────────
		// Step 3: Build ECharts series with resolved visibility
		// ──────────────────────────────────────────────
		let cumulativeValue = 0;

		return series.map((s, idx) => {
			const m = measurements[idx];

			const nSizeStatusLabel = getNSizeStatusLabel(s.n_size);
			const isNoDataNSize = nSizeStatusLabel === NSizeLabelText.NoData;

			const value = m.isNoData ? 0 : convertStringToNumeric(m.formattedVal);

			const resolvedRows =
				!m.isNoData && statTestConfig && m.segmentPixelWidth >= config.statMarkerLayout.minSegmentWidth ? resolveStatMarkerRows(s.stat_marker, statTestConfig) : [];

			const hasMarkers = resolvedRows.length > 0;
			const segmentLeft = cumulativeValue;
			const segmentMidX = segmentLeft + Number(value) / 2;
			cumulativeValue += Number(value);

			const markPointData = hasMarkers ? buildHStackedMarkPoints(resolvedRows, statTestConfig!, segmentMidX, config.labelFontSize, m.segmentPixelWidth) : [];

			markPointData.forEach((pt: any) => {
				pt.dataPointMeta = {
					stat_marker: s.stat_marker ?? null,
					value: s.value,
					n_size: s.n_size,
					name: s.name,
				};
			});

			// Determine text color based on background color contrast
			const bgColor = colors[s.name];
			const isLightBg = bgColor ? isLightColor(bgColor) : false;
			const textColor = isLightBg ? "#000" : config.whiteBg;

			const finalShowLabel = labelVisibility[idx];

			return {
				name: s.name,
				type: "bar" as const,
				stack: "total",
				data: [
					{
						value,
						n_size: isNoDataNSize ? NSizeLabelText.NoData : s.n_size,
						name: s.name,
						displayValue: m.isNoData ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : m.formattedVal,
						stat_marker: s.stat_marker ?? null,
					},
				],
				barWidth,
				barGap: "0%",
				barCategoryGap: "0%",
				itemStyle: {
					color: colors[s.name],
					borderColor: config.whiteBg,
					borderWidth: 0,
				},
				label: {
					show: finalShowLabel,
					position: "inside" as const,
					fontSize: labelFontSize,
					fontWeight: "normal" as const,
					formatter: () => m.formattedVal,
					color: m.isNoData ? SUPPRESSED_VALUE_COLOR : textColor,
					...(hasMarkers ? { offset: [0, 3] } : {}),
				},
				// FIX 5: labelLayout for ECharts 5+ collision avoidance
				// This is a safety net — our pre-pass handles most cases,
				// but labelLayout catches sub-pixel rounding issues.
				labelLayout: {
					hideOverlap: true,
				},
				...(markPointData.length > 0
					? {
						markPoint: {
							symbol: "circle",
							symbolSize: 0,
							data: markPointData,
							animation: false,
							tooltip: { show: true },
						},
					}
					: {}),
			};
		});
	}, [series, labelFormat, xAxisDomain.min, xAxisDomain.max, rowHeight, colors, config, statTestConfig]);

	const hasAnyMarkers = useMemo(() => hasAnyStatMarkers(series, statTestConfig), [series, statTestConfig]);

	const option = useMemo(
		() => ({
			animation: false,
			grid: {
				left: 0,
				right: config.layoutConfig.grid.right / 2,
				top: scaled.geometry.cellPaddingV,
				bottom: scaled.geometry.cellPaddingV,
			},
			tooltip: {
				...getCommonTooltipConfig(),
				formatter: tooltipFormatter,
				renderMode: "html",
				className: "stat-marker-tooltip",
			},
			xAxis: {
				type: "value" as const,
				min: xAxisDomain.min,
				max: xAxisDomain.max,
				show: false,
			},
			yAxis: {
				type: "category" as const,
				data: ["Attr"],
				show: false,
			},
			series: processedSeries,
		}),
		[processedSeries, tooltipFormatter, xAxisDomain.min, xAxisDomain.max, scaled.geometry.cellPaddingH, scaled.geometry.cellPaddingV],
	);

	if (dataStatus === "no-data" || dataStatus === "zero-sum" || dataStatus === "low-n") {
		const isSuppressed = dataStatus === "no-data" || dataStatus === "low-n";
		return (
			<div className="w-full h-full flex items-center justify-start px-4">
				<span className="text-xs" style={{ color: isSuppressed ? SUPPRESSED_VALUE_COLOR : config.mutedText }}>
					{dataStatus === "low-n" ? NSizeLabelText.LowN : SUPPRESSED_VALUE_PLACEHOLDER}
				</span>
			</div>
		);
	}

	return (
		<ReactECharts
			option={option}
			ref={chartRef}
			onEvents={onEvents}
			style={{ width: "100%", height: rowHeight || "100%" }}
			autoResize
			opts={{ renderer: "svg" }}
		/>
	);
};

export default React.memo(MiniHStackedBarChart, (prevProps, nextProps) => {
	return (
		prevProps.series === nextProps.series &&
		prevProps.colors === nextProps.colors &&
		prevProps.labelFormat === nextProps.labelFormat &&
		prevProps.xAxisDomain.min === nextProps.xAxisDomain.min &&
		prevProps.xAxisDomain.max === nextProps.xAxisDomain.max &&
		prevProps.rowHeight === nextProps.rowHeight &&
		prevProps.containerWidth === nextProps.containerWidth &&
		prevProps.chartHeight === nextProps.chartHeight &&
		prevProps.statTestConfig === nextProps.statTestConfig
	);
});
